/**
 * Parse markdown text into structured sections
 * Handles headings, bold text, bullet points, and paragraphs
 */
export function parseMarkdownAdvantages(markdownText) {
  if (!markdownText || typeof markdownText !== 'string') {
    return [];
  }

  const sections = [];
  const lines = markdownText.split('\n');
  let currentSection = null;
  let currentSubsection = null;
  let currentParagraph = '';

  const finishParagraph = () => {
    if (currentParagraph.trim()) {
      if (currentSubsection) {
        currentSubsection.content.push({ type: 'paragraph', text: currentParagraph.trim() });
      } else if (currentSection) {
        currentSection.content.push({ type: 'paragraph', text: currentParagraph.trim() });
      }
      currentParagraph = '';
    }
  };

  const finishSubsection = () => {
    if (currentSubsection) {
      currentSection.content.push(currentSubsection);
      currentSubsection = null;
    }
  };

  const finishSection = () => {
    finishParagraph();
    finishSubsection();
    if (currentSection) {
      sections.push(currentSection);
      currentSection = null;
    }
  };

  for (let line of lines) {
    line = line.trim();

    // Skip empty lines
    if (!line) {
      finishParagraph();
      continue;
    }

    // H2 heading (## )
    if (line.startsWith('## ')) {
      finishSection();
      currentSection = {
        type: 'section',
        title: line.replace(/^## /, '').trim(),
        content: []
      };
      continue;
    }

    // H1 heading (# ) - treat as main title
    if (line.startsWith('# ')) {
      finishSection();
      currentSection = {
        type: 'main-title',
        title: line.replace(/^# /, '').trim(),
        content: []
      };
      continue;
    }

    // Bold subsection (**text:**)
    const boldMatch = line.match(/^\*\*(.+?):?\*\*$/);
    if (boldMatch) {
      finishParagraph();
      finishSubsection();
      currentSubsection = {
        type: 'subsection',
        title: boldMatch[1].trim(),
        content: []
      };
      continue;
    }

    // Bullet point (• or -)
    if (line.startsWith('•') || line.startsWith('- ') || line.startsWith('* ')) {
      finishParagraph();
      const bulletText = line.replace(/^[•\-\*]\s*/, '').trim();

      // Check if bullet has bold prefix like **Program Name:**
      const bulletBoldMatch = bulletText.match(/^\*\*(.+?):?\*\*(.*)$/);

      if (currentSubsection) {
        currentSubsection.content.push({
          type: 'bullet',
          text: bulletText,
          boldPrefix: bulletBoldMatch ? bulletBoldMatch[1].trim() : null,
          mainText: bulletBoldMatch ? bulletBoldMatch[2].trim() : bulletText
        });
      } else if (currentSection) {
        currentSection.content.push({
          type: 'bullet',
          text: bulletText,
          boldPrefix: bulletBoldMatch ? bulletBoldMatch[1].trim() : null,
          mainText: bulletBoldMatch ? bulletBoldMatch[2].trim() : bulletText
        });
      }
      continue;
    }

    // Regular paragraph text
    if (currentParagraph) {
      currentParagraph += ' ' + line;
    } else {
      currentParagraph = line;
    }
  }

  // Finish any remaining content
  finishSection();

  return sections;
}

/**
 * Parse inline markdown formatting (bold, italic, etc.)
 */
export function parseInlineMarkdown(text) {
  if (!text) return text;

  // Replace **bold** with <strong>
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Replace *italic* with <em>
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');

  return text;
}
